import { Card, DatePicker, HStack, Icon, Input, Portal, SimpleGrid, Text, VStack } from '@chakra-ui/react'
import { parseDate } from '@internationalized/date'

import { NativeSelectField, NativeSelectRoot } from '@src/components/chakra-ui/native-select'
import {
  browserTimezone,
  isoToLocalInput,
  localToIso,
  SESSION_HALF_HOUR_SLOTS,
  timezoneAbbreviation,
} from '@src/components/sco/Advisors/utils/sessionFormat'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LuCalendar, LuClock, LuGlobe } from 'react-icons/lu'

/** Backend accepts three proposed slots (`ADVISOR_SESSION_SUGGESTED_TIMES_COUNT`). */
export const SUGGESTED_TIMES_COUNT = 3

const EMPTY_TIMES: string[] = Array.from({ length: SUGGESTED_TIMES_COUNT }, () => '')

/** Default book slots: tomorrow at 10:00, 13:00, 15:00 in the viewer's timezone. */
export const DEFAULT_SLOT_HOURS = [10, 13, 15] as const

const pad2 = (n: number) => String(n).padStart(2, '0')

const ymdFromDate = (date: Date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

export const defaultSuggestedLocalTimes = (now = new Date()): string[] => {
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
  const ymd = ymdFromDate(tomorrow)
  return DEFAULT_SLOT_HOURS.map(hour => `${ymd}T${pad2(hour)}:00`)
}

const todayYmd = () => ymdFromDate(new Date())

const splitLocalInput = (value: string): { date: string; time: string } => {
  const [date = '', time = ''] = value.split('T')
  return { date, time: time.slice(0, 5) }
}

/** Full local day, 30-minute steps. Not limited to business hours. */
const TIME_SLOTS = SESSION_HALF_HOUR_SLOTS

const formatClockLabel = (hhmm: string) => {
  const match = TIME_SLOTS.find(slot => slot.value === hhmm)
  if (match) {
    return match.label
  }
  const [hour, minute] = hhmm.split(':').map(Number)
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return hhmm
  }
  return new Date(2000, 0, 1, hour, minute).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Compact clock for headlines: `10am`, `1:30pm`. */
export const formatFriendlyClock = (hhmm: string): string => {
  const [hour, minute] = hhmm.split(':').map(Number)
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return hhmm
  }
  const suffix = hour >= 12 ? 'pm' : 'am'
  const hour12 = hour % 12 || 12
  return minute === 0 ? `${hour12}${suffix}` : `${hour12}:${pad2(minute)}${suffix}`
}

const startOfLocalDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

/**
 * Card headline: `Tomorrow 10am` / `Today 1:30pm` / `Tue Aug 25, 10am`.
 */
export const formatSuggestedSlotHeadline = (localInput: string, now = new Date()): string => {
  const { date, time } = splitLocalInput(localInput)
  if (!date || !time) {
    return time ? formatFriendlyClock(time) : 'Pick a time'
  }
  const [year, month, day] = date.split('-').map(Number)
  const slotDay = new Date(year, month - 1, day)
  if (Number.isNaN(slotDay.getTime())) {
    return formatFriendlyClock(time)
  }
  const clock = formatFriendlyClock(time)
  const today = startOfLocalDay(now)
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)
  const slotStart = startOfLocalDay(slotDay)

  if (slotStart.getTime() === today.getTime()) {
    return `Today ${clock}`
  }
  if (slotStart.getTime() === tomorrow.getTime()) {
    return `Tomorrow ${clock}`
  }

  const weekday = slotDay.toLocaleDateString(undefined, { weekday: 'short' })
  const monthDay = slotDay.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return `${weekday} ${monthDay}, ${clock}`
}

export type SuggestedTimes = {
  /** `datetime-local` values, one per slot. */
  values: string[]
  setAt: (index: number, value: string) => void
  /** Non-empty, parseable slots as ISO strings — what the mutations take. */
  isoValues: string[]
  /** Seed the inputs from a session's stored `suggestedTime1..3`. */
  reset: (isoTimes: (string | null | undefined)[]) => void
}

/**
 * State for the three proposed-time inputs shared by Book and Session detail.
 *
 * `initialIsoTimes` seeds the inputs once on mount, so callers do not need a
 * "have I hydrated yet" flag plus an effect to copy server data into state.
 * Book (no seed) starts at tomorrow 10am / 1pm / 3pm.
 */
export const useSuggestedTimes = (initialIsoTimes?: (string | null | undefined)[]): SuggestedTimes => {
  const [values, setValues] = useState<string[]>(() => {
    const seeded = EMPTY_TIMES.map((_, index) => isoToLocalInput(initialIsoTimes?.[index]))
    return seeded.some(Boolean) ? seeded : defaultSuggestedLocalTimes()
  })

  const setAt = useCallback((index: number, value: string) => {
    setValues(current => current.map((existing, i) => (i === index ? value : existing)))
  }, [])

  const reset = useCallback((isoTimes: (string | null | undefined)[]) => {
    const seeded = EMPTY_TIMES.map((_, index) => isoToLocalInput(isoTimes[index]))
    setValues(seeded.some(Boolean) ? seeded : defaultSuggestedLocalTimes())
  }, [])

  const isoValues = useMemo(
    () => values.map(localToIso).filter((value): value is string => !!value),
    [values]
  )

  return { values, setAt, isoValues, reset }
}

const SlotDatePicker = ({
  date,
  minDate,
  timeZone,
  onDateChange,
}: {
  date: string
  minDate: string
  timeZone: string
  onDateChange: (ymd: string) => void
}) => {
  const value = useMemo(() => {
    try {
      return date ? [parseDate(date)] : []
    } catch {
      return []
    }
  }, [date])
  const min = useMemo(() => {
    try {
      return parseDate(minDate)
    } catch {
      return undefined
    }
  }, [minDate])

  return (
    <DatePicker.Root
      closeOnSelect
      openOnClick
      min={min}
      positioning={{ placement: 'bottom-start' }}
      size={'md'}
      timeZone={timeZone}
      value={value}
      variant={'subtle'}
      width={'full'}
      onValueChange={details => {
        const next = details.value[0]
        if (!next) {
          return
        }
        onDateChange(`${next.year}-${pad2(next.month)}-${pad2(next.day)}`)
      }}
    >
      <DatePicker.Control width={'full'}>
        <Input asChild size={'md'} variant={'subtle'}>
          <DatePicker.Input
            bg={'colorPalette.bg/50'}
            borderColor={'colorPalette.border/80'}
            borderWidth={'1px'}
            pe={'10'}
            placeholder={'Pick a date'}
          />
        </Input>
        <DatePicker.IndicatorGroup>
          <DatePicker.Trigger aria-label={'Open calendar'}>
            <LuCalendar />
          </DatePicker.Trigger>
        </DatePicker.IndicatorGroup>
      </DatePicker.Control>
      <Portal>
        <DatePicker.Positioner>
          <DatePicker.Content>
            <DatePicker.View view={'day'}>
              <DatePicker.Header />
              <DatePicker.DayTable />
            </DatePicker.View>
            <DatePicker.View view={'month'}>
              <DatePicker.Header />
              <DatePicker.MonthTable />
            </DatePicker.View>
            <DatePicker.View view={'year'}>
              <DatePicker.Header />
              <DatePicker.YearTable />
            </DatePicker.View>
          </DatePicker.Content>
        </DatePicker.Positioner>
      </Portal>
    </DatePicker.Root>
  )
}

/**
 * Three proposed session slots, labelled with the viewer's timezone.
 *
 * The expert may publish preferred weekly hours; those are informational only.
 * The seeker still proposes times and the expert accepts one.
 */
export const SuggestedTimesFields = ({
  times,
  label = 'Suggested times',
  helperText,
  preferenceNote,
}: {
  times: SuggestedTimes
  label?: string
  helperText?: string
  /** Expert preferred days/hours. Informational — never used to block a slot. */
  preferenceNote?: string | null
}) => {
  const [timezoneAbbr, setTimezoneAbbr] = useState('')
  const [viewerTimeZone, setViewerTimeZone] = useState('UTC')
  useEffect(() => {
    setTimezoneAbbr(timezoneAbbreviation())
    setViewerTimeZone(browserTimezone())
  }, [])
  const minDate = todayYmd()

  const setDateAt = (index: number, ymd: string) => {
    const { time } = splitLocalInput(times.values[index] || '')
    times.setAt(index, `${ymd}T${time || '10:00'}`)
  }

  const setTimeAt = (index: number, hhmm: string) => {
    const { date } = splitLocalInput(times.values[index] || '')
    times.setAt(index, `${date || minDate}T${hhmm}`)
  }

  return (
    <VStack align={'stretch'} gap={3}>
      <HStack gap={2} justify={'space-between'} wrap={'wrap'}>
        <Text color={'colorPalette.fg/60'} textStyle={'labelXs'}>
          {label}
        </Text>
        <HStack color={'colorPalette.fg/50'} gap={1}>
          <Icon boxSize={3.5}>
            <LuGlobe />
          </Icon>
          <Text fontSize={'xs'}>Your local time{timezoneAbbr ? ` (${timezoneAbbr})` : ''}</Text>
        </HStack>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 3 }} gap={3}>
        {times.values.map((value, index) => {
          const { date, time } = splitLocalInput(value)
          const timeOptions = TIME_SLOTS.some(slot => slot.value === time)
            ? TIME_SLOTS
            : time
              ? [{ value: time, label: formatClockLabel(time) }, ...TIME_SLOTS]
              : TIME_SLOTS

          return (
            <Card.Root key={index} size={'sm'} variant={'muted'}>
              <Card.Body gap={3}>
                <VStack align={'stretch'} gap={0.5}>
                  <Text color={'colorPalette.fg/55'} textStyle={'labelXs'}>
                    Option {index + 1}
                  </Text>
                  <Text fontWeight={'semibold'} textStyle={'sm'}>
                    {formatSuggestedSlotHeadline(value)}
                  </Text>
                </VStack>

                <VStack align={'stretch'} gap={2}>
                  <HStack color={'colorPalette.fg/50'} gap={1.5}>
                    <Icon boxSize={3.5}>
                      <LuCalendar />
                    </Icon>
                    <Text fontSize={'xs'}>Date</Text>
                  </HStack>
                  <SlotDatePicker
                    date={date}
                    minDate={minDate}
                    timeZone={viewerTimeZone}
                    onDateChange={ymd => setDateAt(index, ymd)}
                  />
                </VStack>

                <VStack align={'stretch'} gap={2}>
                  <HStack color={'colorPalette.fg/50'} gap={1.5}>
                    <Icon boxSize={3.5}>
                      <LuClock />
                    </Icon>
                    <Text fontSize={'xs'}>Time</Text>
                  </HStack>
                  <NativeSelectRoot size={'md'} variant={'subtle'}>
                    <NativeSelectField
                      aria-label={`Option ${index + 1} time`}
                      items={timeOptions}
                      value={time}
                      onChange={event => setTimeAt(index, event.currentTarget.value)}
                    />
                  </NativeSelectRoot>
                </VStack>
              </Card.Body>
            </Card.Root>
          )
        })}
      </SimpleGrid>

      {preferenceNote ? (
        <Text color={'colorPalette.fg/60'} fontFamily={'longform'} fontSize={'sm'}>
          {preferenceNote}
        </Text>
      ) : null}

      {helperText && (
        <Text color={'colorPalette.fg/55'} fontFamily={'longform'} fontSize={'xs'}>
          {helperText}
        </Text>
      )}
    </VStack>
  )
}
